/**
 * AUTHORING editor for a course: meta (title/description/category), structure
 * course→unit→lesson→block with the 6 types, and PUBLISH to the catalog. Writes
 * to PowerSync (local-first); the server applies author/editor authz and seals the
 * course version on every content change.
 */
import { type Component, createMemo, createSignal, For, Show } from "solid-js";
import { createStore } from "solid-js/store";
import { msg } from "@lingui/core/macro";
import { Plus, Trash2 } from "@fleet/icons";
import {
  addBlockMutation,
  addLessonMutation,
  addUnitMutation,
  deleteBlockMutation,
  deleteLessonMutation,
  deleteUnitMutation,
  parseBlockContent,
  publishCourseMutation,
  unpublishCourseMutation,
  updateBlockMutation,
  updateCourseMutation,
  updateLessonMutation,
  updateUnitMutation,
} from "@aula/shared";
import type { Lesson } from "@aula/powersync";
import { Link } from "@tanstack/solid-router";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@fleet/ui";
import { showToast } from "@/components/ui/toast";
import { useCourseTree, useCourseWrite, useLessonBlocks } from "../../hooks/use-course-data";
import { BLOCK_KINDS, defaultDefFor } from "../../lib/registry";
import { BlockEditor } from "./block-editor";

const field =
  "w-full rounded-lg border-2 border-border bg-field-background px-3 py-2 focus-visible:border-primary focus-visible:outline-none";

export const CourseEditor: Component<{ courseId: string }> = (props) => {
  const { t } = useI18n();
  const courseId = () => props.courseId;
  const { drizzle } = useCourseWrite();
  const { course, units, lessons } = useCourseTree(courseId);
  const [selectedLesson, setSelectedLesson] = createSignal<string | null>(null);

  // Block edit drafts, HOISTED to the editor root (which does NOT
  // remount). With sync active, the tree queries (units/lessons/blocks)
  // re-emit with new references on every tick → the internal `<For>`
  // recreates its rows and `LessonBlocks` would remount, losing its local
  // edit state (inputs snapped back to the server `content` mid-typing).
  // Keeping it here, editing survives sync re-emissions.
  const [blockDrafts, setBlockDrafts] = createStore<Record<string, Record<string, unknown>>>({});

  const db = () => drizzle();
  const isPublished = () => course.data?.visibility === "public";

  /**
   * Fires a verified write mutation (`patchRowVerified`, see
   * `courses/mutations.ts` §4.11) without waiting for it to land, but WITHOUT dropping
   * the rejection: now that `course`/`unit`/`lesson`/`block` RE-READ the PowerSync
   * view and can throw `LocalWriteLostError`, a bare `void mutation(...)` would
   * leave the failure as an unhandled rejection and the field would keep
   * showing a value on screen that never actually saved.
   */
  const fireVerified = (promise: Promise<unknown>, errorMessage: string): void => {
    void promise.catch(() => {
      showToast(errorMessage, "error");
    });
  };

  const lessonsByUnit = createMemo(() => {
    const map = new Map<string, Lesson[]>();
    for (const l of lessons.data ?? []) {
      const arr = map.get(l.unit_id ?? "") ?? [];
      arr.push(l);
      map.set(l.unit_id ?? "", arr);
    }
    return map;
  });

  const saveMeta = (patch: Record<string, unknown>) => {
    const d = db();
    if (d) {
      fireVerified(updateCourseMutation(d, props.courseId, patch), t(msg`Could not save`));
    }
  };

  /**
   * Publish/unpublish. Both mutations RE-READ the row to confirm the
   * change landed (`patchRowVerified`), so they can now throw: without this
   * try/catch the rejection would surface as an unhandled rejection and the button would keep
   * showing the state as if it had changed while the local course is still
   * exactly as it was.
   */
  const togglePublish = async () => {
    const d = db();
    if (!d) {
      return;
    }
    try {
      if (isPublished()) {
        await unpublishCourseMutation(d, props.courseId);
        showToast(t(msg`Removed from the catalog`), "success");
      } else {
        await publishCourseMutation(d, props.courseId);
        showToast(t(msg`Published to the public catalog`), "success");
      }
    } catch {
      showToast(t(msg`Could not update the catalog status`), "error");
    }
  };

  const addUnit = () => {
    const d = db();
    if (d) {
      void addUnitMutation(d, props.courseId, { position: units.data?.length ?? 0 });
    }
  };
  const addLesson = (unitId: string) => {
    const d = db();
    if (d) {
      void addLessonMutation(d, {
        courseId: props.courseId,
        unitId,
        position: lessonsByUnit().get(unitId)?.length ?? 0,
      });
    }
  };

  return (
    <div class="mx-auto max-w-3xl space-y-6 p-4 md:p-6">
      <Link to="/authoring" class="text-sm text-muted-foreground hover:text-foreground">
        ← {t(msg`My courses`)}
      </Link>

      <Show when={!course.isLoading} fallback={<Skeleton class="h-40 w-full rounded-2xl" />}>
        {/* Meta */}
        <div class="space-y-3 rounded-2xl border-2 border-border p-5">
          {/* Local-first: persistimos en cada tecla (onInput), NO al blur (onChange).
              Un curso recién creado se sella en el servidor con su título por
              defecto ("Nuevo curso") y ese eco vuelve por sync ~1s después; si el
              texto en curso sólo se persistiera al blur, el eco RE-PINTA el input
              controlado (value={"{course.data.title}"}) y descarta lo tecleado.
              Al escribir en cada input, el op queda en la cola CRUD (que PowerSync
              preserva por encima de los datos sincronizados) antes de que llegue el
              eco → la edición sobrevive. */}
          <input
            class={`${field} text-xl font-bold`}
            data-testid="edit-course-title"
            value={course.data?.title ?? ""}
            onInput={(e) => saveMeta({ title: e.currentTarget.value })}
            placeholder={t(msg`Course title`)}
          />
          <textarea
            class={field}
            data-testid="edit-course-description"
            rows={2}
            value={course.data?.description ?? ""}
            onInput={(e) => saveMeta({ description: e.currentTarget.value })}
            placeholder={t(msg`Description`)}
          />
          <input
            class={field}
            data-testid="edit-course-category"
            value={course.data?.category ?? ""}
            onInput={(e) => saveMeta({ category: e.currentTarget.value })}
            placeholder={t(msg`Category (e.g. Languages)`)}
          />
          <div class="flex items-center justify-between pt-1">
            <span class="text-sm text-muted-foreground">
              {t(msg`Status`)}:{" "}
              {/* data-published: señal LOCALE-AGNÓSTICA para e2e (el texto está traducido). */}
              <span
                data-testid="course-visibility"
                data-published={isPublished() ? "true" : "false"}
              >
                {isPublished() ? t(msg`Public`) : t(msg`Draft`)}
              </span>{" "}
              · {t(msg`version {version}`, { version: course.data?.version ?? 1 })}
            </span>
            <Button
              data-testid="course-publish-toggle"
              variant={isPublished() ? "outline" : "default"}
              onClick={togglePublish}
            >
              {isPublished() ? t(msg`Unpublish`) : t(msg`Publish`)}
            </Button>
          </div>
        </div>

        {/* Estructura */}
        <div class="space-y-4" data-testid="editor-units">
          <For each={units.data ?? []}>
            {(u) => (
              <div class="rounded-2xl border border-border p-4">
                <div class="mb-2 flex items-center gap-2">
                  <input
                    class={`${field} font-semibold`}
                    data-testid="edit-unit-title"
                    value={u.title ?? ""}
                    onInput={(e) => {
                      const d = db();
                      if (d) {
                        fireVerified(
                          updateUnitMutation(d, u.id, { title: e.currentTarget.value }),
                          t(msg`Could not save the unit`),
                        );
                      }
                    }}
                  />
                  <button
                    type="button"
                    data-testid="delete-unit"
                    onClick={() => {
                      const d = db();
                      if (d) {
                        fireVerified(
                          deleteUnitMutation(d, u.id),
                          t(msg`Could not delete the unit`),
                        );
                      }
                    }}
                    class="text-muted-foreground hover:text-danger"
                  >
                    <Trash2 class="h-4 w-4" />
                  </button>
                </div>
                <ul class="space-y-1">
                  <For each={lessonsByUnit().get(u.id) ?? []}>
                    {(l) => (
                      <li>
                        <div class="flex items-center gap-2">
                          <button
                            type="button"
                            data-testid="select-lesson"
                            onClick={() =>
                              setSelectedLesson(selectedLesson() === l.id ? null : l.id)
                            }
                            class={`flex-1 rounded-lg border px-3 py-2 text-left text-sm ${selectedLesson() === l.id ? "border-primary bg-primary/5" : "border-transparent hover:bg-muted/50"}`}
                          >
                            {l.title}
                          </button>
                          <input
                            type="number"
                            class="w-16 rounded-lg border-2 border-border bg-field-background px-2 py-1 text-sm"
                            data-testid="edit-lesson-xp"
                            value={l.xp_reward ?? 10}
                            onChange={(e) => {
                              const d = db();
                              if (d) {
                                fireVerified(
                                  updateLessonMutation(d, l.id, {
                                    xp_reward: Number(e.currentTarget.value),
                                  }),
                                  t(msg`Could not save the lesson`),
                                );
                              }
                            }}
                          />
                          <button
                            type="button"
                            data-testid="delete-lesson"
                            onClick={() => {
                              const d = db();
                              if (d) {
                                fireVerified(
                                  deleteLessonMutation(d, l.id),
                                  t(msg`Could not delete the lesson`),
                                );
                              }
                            }}
                            class="text-muted-foreground hover:text-danger"
                          >
                            <Trash2 class="h-4 w-4" />
                          </button>
                        </div>
                        <Show when={selectedLesson() === l.id}>
                          <div class="mt-2">
                            <input
                              class={`${field} mb-2 text-sm`}
                              data-testid="edit-lesson-title"
                              value={l.title ?? ""}
                              onInput={(e) => {
                                const d = db();
                                if (d) {
                                  fireVerified(
                                    updateLessonMutation(d, l.id, { title: e.currentTarget.value }),
                                    t(msg`Could not save the lesson`),
                                  );
                                }
                              }}
                            />
                            <LessonBlocks
                              courseId={props.courseId}
                              lessonId={l.id}
                              drafts={blockDrafts}
                              setDraft={(id, def) => setBlockDrafts(id, def)}
                            />
                          </div>
                        </Show>
                      </li>
                    )}
                  </For>
                </ul>
                <button
                  type="button"
                  data-testid="add-lesson"
                  onClick={() => addLesson(u.id)}
                  class="mt-2 flex items-center gap-1 text-sm text-primary hover:underline"
                >
                  <Plus class="h-3 w-3" /> {t(msg`Add lesson`)}
                </button>
              </div>
            )}
          </For>
          <Button variant="outline" data-testid="add-unit" onClick={addUnit} class="gap-2">
            <Plus class="h-4 w-4" /> {t(msg`Add unit`)}
          </Button>
        </div>
      </Show>
    </div>
  );
};

/** Blocks of a lesson: create (pick a type), edit `def`, save, delete. */
const LessonBlocks: Component<{
  courseId: string;
  lessonId: string;
  /** Drafts hoisted to the root (survive sync re-emissions). */
  drafts: Record<string, Record<string, unknown>>;
  setDraft: (id: string, def: Record<string, unknown>) => void;
}> = (props) => {
  const { t } = useI18n();
  const { drizzle } = useCourseWrite();
  const blocks = useLessonBlocks(() => props.lessonId);

  const db = () => drizzle();
  // PURE read (no effects): the live draft or, if not yet edited, the server-parsed
  // `def`. The first edit seeds the draft via `setDraft`
  // (the BlockEditor `patch` spreads this full `def` before applying the
  // change), so we never lose the default options/fields.
  const draftFor = (id: string, content: unknown): Record<string, unknown> =>
    props.drafts[id] ?? parseBlockContent(content);

  const addBlock = (kind: string) => {
    const d = db();
    if (d) {
      void addBlockMutation(d, {
        courseId: props.courseId,
        lessonId: props.lessonId,
        kind,
        content: defaultDefFor(kind),
        position: blocks.data?.length ?? 0,
      });
    }
  };
  const saveBlock = async (id: string, content: unknown) => {
    const d = db();
    if (!d) {
      return;
    }
    // Wait for the LOCAL (WASQLite) write to confirm BEFORE announcing the
    // save. If it announced earlier (fire-and-forget), a `Save` followed by an
    // immediate navigation (hard reload / deep-link) would abort the transaction and
    // the block would keep its default `def` (empty prompt) → grading 0%.
    // The `testid` lets e2e wait deterministically for DURABILITY.
    //
    // `updateBlockMutation` RE-READS the view to confirm the save
    // (`patchRowVerified`, §4.11) and can now throw `LocalWriteLostError`; the
    // caller (`onClick={() => void saveBlock(...)}`) does not chain `.catch`, so
    // without this try/catch the rejection would surface as an unhandled rejection and the
    // "Save" button would never warn that the content did not save.
    try {
      await updateBlockMutation(d, id, { content: draftFor(id, content) });
      showToast(t(msg`Block saved`), "success", { testid: "block-saved" });
    } catch {
      showToast(t(msg`Could not save the block`), "error");
    }
  };

  return (
    <div class="space-y-3 rounded-lg bg-muted/30 p-3" data-testid="lesson-blocks">
      <For each={blocks.data ?? []}>
        {(b) => (
          <div class="rounded-lg border border-border bg-background p-3" data-testid="editor-block">
            <div class="mb-2 flex items-center justify-between">
              <span class="rounded-full bg-accent px-2 py-0.5 text-xs text-accent-foreground">
                {b.kind}
              </span>
              <div class="flex gap-2">
                <button
                  type="button"
                  data-testid="save-block"
                  onClick={() => void saveBlock(b.id, b.content)}
                  class="text-xs text-primary hover:underline"
                >
                  {t(msg`Save`)}
                </button>
                <button
                  type="button"
                  data-testid="delete-block"
                  onClick={() => {
                    const d = db();
                    if (d) {
                      // `deleteBlockMutation` RE-READS after the soft-delete (§4.11) and
                      // can throw; without `.catch` it would be an unhandled rejection.
                      void deleteBlockMutation(d, b.id).catch(() => {
                        showToast(t(msg`Could not delete the block`), "error");
                      });
                    }
                  }}
                  class="text-muted-foreground hover:text-danger"
                >
                  <Trash2 class="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
            <BlockEditor
              kind={b.kind ?? "quiz"}
              def={draftFor(b.id, b.content)}
              onChange={(def) => props.setDraft(b.id, def)}
            />
          </div>
        )}
      </For>
      <div class="flex flex-wrap gap-1.5">
        <For each={BLOCK_KINDS}>
          {(k) => (
            <button
              type="button"
              data-testid={`add-block-${k.kind}`}
              onClick={() => addBlock(k.kind)}
              class="rounded-lg border border-border px-2.5 py-1 text-xs hover:border-primary/50"
              title={t(k.hint)}
            >
              + {t(k.label)}
            </button>
          )}
        </For>
      </div>
    </div>
  );
};
